import { useState, useEffect, useRef, useCallback } from "react";
import { Search, FileText, X, ArrowRight, FolderOpen, Camera, Plus, Pencil, Trash2, Calendar, Link2, Copy, RefreshCw, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "../api";
import { printReport, openSavedReport } from "../lib/generateReport";
import { useConfirm } from "../components/ConfirmDialog.jsx";
import { useToast } from "../components/Toast.jsx";
import FileThumb from "../components/FileThumb.jsx";
import CameraCaptureModal from "../components/CameraCaptureModal.jsx";
import PhotoLightboxModal from "../components/PhotoLightboxModal.jsx";
import ReassignPatientModal from "../components/ReassignPatientModal.jsx";
import { useAuth } from "../contexts/AuthContext.jsx";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";
import { buildAnnotatedFootCrops } from "../lib/scanPreview.js";
import PatientAlias from "../components/PatientAlias.jsx";
import LoadError from "../components/LoadError.jsx";

const PAGE_SIZE = 60;

function mmToRuSize(mm) {
  if (!mm || isNaN(Number(mm))) return null;
  return Math.round(Number(mm) / 10 * 1.5 + 1);
}

// Compact "one control instead of two always-visible date inputs" filter — click-outside-to-close
// popover, same hand-rolled pattern as the patient search dropdown in App.jsx (no Popover
// component exists in components/ui/ yet). A single filled date (either box) filters that one day
// rather than an open-ended "from that day on".
function DateFilterControl({ dateFrom, dateTo, onApply }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(dateFrom);
  const [to, setTo] = useState(dateTo);
  const ref = useRef(null);

  useEffect(() => { setFrom(dateFrom); setTo(dateTo); }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const formatDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("ru-RU");
  const label = !dateFrom && !dateTo
    ? "Все даты"
    : dateFrom && dateTo && dateFrom !== dateTo
      ? `${formatDate(dateFrom)} – ${formatDate(dateTo)}`
      : formatDate(dateFrom || dateTo);

  const apply = () => {
    if (from && !to) onApply(from, from);
    else if (!from && to) onApply(to, to);
    else onApply(from, to);
    setOpen(false);
  };
  const clear = () => { setFrom(""); setTo(""); onApply("", ""); setOpen(false); };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`h-8 flex items-center gap-1.5 rounded-md border px-2.5 text-xs whitespace-nowrap ${(dateFrom || dateTo) ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}
      >
        <Calendar className="h-3.5 w-3.5" />
        {label}
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 z-50 w-64 bg-white border border-slate-200 rounded-xl shadow-lg p-3 flex flex-col gap-2.5">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-slate-400 uppercase tracking-wide">С</label>
            <input type="date" className="h-8 rounded-md border border-slate-200 px-2 text-xs" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-slate-400 uppercase tracking-wide">По (необязательно — для одной даты)</label>
            <input type="date" className="h-8 rounded-md border border-slate-200 px-2 text-xs" value={to} onChange={e => setTo(e.target.value)} />
          </div>
          <div className="flex gap-2 mt-1">
            <Button size="sm" className="flex-1" onClick={apply}>Применить</Button>
            <Button size="sm" variant="ghost" onClick={clear}>Сбросить</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DocumentsPage({ onNavigateToPatient, onEditDocument }) {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const requestRef = useRef(0);
  const [query, setQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null);
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { showToast, ToastElement } = useToast();

  const debouncedQuery = useDebouncedValue(query, 300);
  const hasFilters = !!(debouncedQuery || locationFilter || dateFrom || dateTo);

  const load = useCallback(() => {
    const request = ++requestRef.current;
    setLoading(true);
    setLoadingMore(false);
    setError("");
    api.listDocuments({ limit: PAGE_SIZE, offset: 0, q: debouncedQuery, location: locationFilter, dateFrom, dateTo })
      .then(d => { if (request === requestRef.current) { setDocs(d.items); setTotal(d.total); } })
      .catch(err => { if (request === requestRef.current) setError(err.response?.data?.error || "Не удалось загрузить сканирования"); })
      .finally(() => { if (request === requestRef.current) setLoading(false); });
  }, [debouncedQuery, locationFilter, dateFrom, dateTo]);

  const cancelRequests = useCallback(() => { requestRef.current++; }, []);
  useEffect(() => { load(); return cancelRequests; }, [load, cancelRequests]);

  useEffect(() => { api.listLocations().then(setLocations).catch(() => {}); }, []);

  const loadMore = () => {
    const request = requestRef.current;
    setLoadingMore(true);
    setError("");
    api.listDocuments({ limit: PAGE_SIZE, offset: docs.length, q: debouncedQuery, location: locationFilter, dateFrom, dateTo })
      .then(d => { if (request === requestRef.current) { setDocs(prev => [...prev, ...d.items]); setTotal(d.total); } })
      .catch(err => { if (request === requestRef.current) setError(err.response?.data?.error || "Не удалось загрузить следующие сканирования"); })
      .finally(() => { if (request === requestRef.current) setLoadingMore(false); });
  };

  const handleDelete = async (id) => {
    if (!(await confirm("Удалить скан без возможности восстановления?", { danger: true }))) return;
    try {
      await api.deleteDocument(id);
      setDocs(prev => prev.filter(d => d.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch {
      showToast("Не удалось удалить скан. Попробуйте ещё раз.");
    }
  };

  return (
    <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white flex-shrink-0 gap-3 flex-wrap">
        <h2 className="text-lg font-bold text-slate-900">Сканирования</h2>
        <div className="flex items-center gap-3">
          {locations.length > 0 && (
            <select
              className="h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
              value={locationFilter}
              onChange={e => setLocationFilter(e.target.value)}
            >
              <option value="">Все точки</option>
              {locations.map(loc => <option key={loc.id} value={loc.name}>{loc.name}</option>)}
            </select>
          )}
          <DateFilterControl dateFrom={dateFrom} dateTo={dateTo} onApply={(f, t) => { setDateFrom(f); setDateTo(t); }} />
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              className="h-8 w-56 pl-8 text-xs border-slate-200 bg-slate-50"
              type="text"
              placeholder="Поиск..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5">
        <LoadError message={error} onRetry={load} retrying={loading} />
        {loading && (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-400">
            <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin" />
            Загрузка...
          </div>
        )}

        {!loading && !error && total === 0 && !hasFilters && (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <FolderOpen className="h-8 w-8 text-slate-300" />
            <p className="text-sm font-semibold text-slate-700">Нет сканирований</p>
            <p className="text-xs text-slate-400">Сохранённые сканирования появятся здесь после первого сканирования</p>
          </div>
        )}

        {!loading && !error && total === 0 && hasFilters && (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Search className="h-8 w-8 text-slate-300" />
            <p className="text-sm font-semibold text-slate-700">Ничего не найдено</p>
            <p className="text-xs text-slate-400">Нет сканирований по запросу «{query}»</p>
          </div>
        )}

        {docs.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  <th className="px-4 py-2.5">Дата</th>
                  <th className="px-4 py-2.5">Пациент</th>
                  <th className="px-4 py-2.5">Размер</th>
                  <th className="px-4 py-2.5">Точка</th>
                  <th className="px-4 py-2.5">Провёл</th>
                  <th className="px-4 py-2.5">Отметки</th>
                  <th className="px-4 py-2.5 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => (
                  <tr
                    key={doc.id}
                    className="group border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => setSelected(doc)}
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {doc.syncedAt && (
                          <span className="h-1.5 w-1.5 rounded-full bg-green-400 flex-shrink-0" title={`Синхронизировано: ${new Date(doc.syncedAt).toLocaleString("ru-RU")}`} />
                        )}
                        {new Date(doc.createdAt).toLocaleString("ru-RU")}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-700">{doc.patientName ? <>{doc.patientName}<PatientAlias name={doc.patientName} alias={doc.patientAlias} /></> : <span className="font-normal text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{doc.footSizeMm ? `${doc.footSizeMm} мм (RU ${mmToRuSize(doc.footSizeMm)})` : <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5">{doc.location || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{doc.operatorName || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5">
                      {doc.diseases?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {doc.diseases.slice(0, 2).map(d => (
                            <span key={d} className="inline-block px-2 py-0.5 bg-red-50 text-red-800 rounded-full text-[10px] font-medium">{d}</span>
                          ))}
                          {doc.diseases.length > 2 && <span className="text-[10px] text-slate-400">+{doc.diseases.length - 2}</span>}
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {doc.hasReport && (
                          <button
                            className="h-7 w-7 flex items-center justify-center rounded-md text-blue-600 hover:bg-blue-50"
                            title="Отчёт сохранён"
                            onClick={async e => {
                              e.stopPropagation();
                              const full = await api.getDocument(doc.id);
                              if (full.reportHtml) openSavedReport(full.reportHtml);
                            }}
                          >
                            <FileText className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {user?.isCentral && (
                          <button
                            className="h-7 w-7 flex items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
                            title="Удалить"
                            onClick={e => { e.stopPropagation(); handleDelete(doc.id); }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && docs.length < total && (
          <div className="flex justify-center mt-5">
            <Button variant="ghost" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Загрузка..." : `Показать ещё (${total - docs.length})`}
            </Button>
          </div>
        )}
      </div>

      {selected && (
        <DocModal
          doc={selected}
          onClose={() => setSelected(null)}
          onNavigateToPatient={onNavigateToPatient}
          onEdit={onEditDocument ? () => { setSelected(null); onEditDocument(selected); } : null}
          onReassigned={(newPatient) => setDocs(prev => prev.map(d =>
            d.id === selected.id ? { ...d, patientId: newPatient.id, patientName: newPatient.name, patientPhone: newPatient.phone } : d
          ))}
        />
      )}
      <ConfirmDialogElement />
      <ToastElement />
    </div>
  );
}

function PhotosModal({ docId, files, onFilesChange, onCameraClick, onPhotoClick, onClose, confirm }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleAdd = async (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selected.length) return;
    setUploading(true);
    try {
      const read = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ name: file.name, base64: reader.result });
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const loaded = await Promise.all(selected.map(read));
      const created = await api.uploadDocumentFiles(docId, loaded);
      onFilesChange(prev => [...prev, ...created]);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!(await confirm("Удалить файл без возможности восстановления?", { danger: true }))) return;
    await api.deleteDocumentFile(docId, fileId);
    onFilesChange(prev => prev.filter(f => f.id !== fileId));
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4"
      onMouseDown={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col" style={{ maxWidth: 720, maxHeight: "85vh" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <span className="text-base font-bold text-slate-900">Фото ({files.length})</span>
          <div className="flex items-center gap-2">
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleAdd} />
            <Button size="sm" variant="ghost" className="gap-1.5" onClick={onCameraClick}>
              <Camera className="h-3.5 w-3.5" />
              Снять камерой
            </Button>
            <Button size="sm" className="gap-1.5" disabled={uploading} onClick={() => inputRef.current?.click()}>
              <Plus className="h-3.5 w-3.5" />
              {uploading ? "Загрузка..." : "Добавить"}
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="px-5 py-4 overflow-y-auto">
          {files.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400">
              <Camera className="h-8 w-8" />
              <span className="text-sm">Пока нет фото — нажмите «Добавить»</span>
            </div>
          ) : (
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}>
              {files.map((f, i) => (
                <div key={f.id} className="relative group">
                  <button onClick={() => onPhotoClick(i)} className="block w-full">
                    <FileThumb src={api.documentFileUrl(docId, f.id)} alt={f.originalName || "Фото"} className="w-full aspect-square rounded-lg border border-slate-200" />
                  </button>
                  <button
                    onClick={() => handleDelete(f.id)}
                    className="absolute top-1.5 right-1.5 h-6 w-6 flex items-center justify-center rounded-full bg-white/90 border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Удалить файл"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DocModal({ doc, patient: patientProp, onClose, onNavigateToPatient, onEdit, onReassigned }) {
  const fileUrl = api.fileUrl(doc.id);
  const { user } = useAuth();
  const [imgFailed, setImgFailed] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  // null | "list" | "camera" | { lightboxIndex } — same one-modal-at-a-time pattern as the
  // Scanner's photoView (App.jsx) — opening the camera or a fullscreen photo replaces the list
  // instead of stacking a modal on top of it.
  const [photoView, setPhotoView] = useState(null);
  const [showReassign, setShowReassign] = useState(false);
  // Callers that already have the full patient object (e.g. PatientCard, which is that patient's
  // own page) pass it directly; DocumentsPage doesn't preload every patient anymore, so it just
  // passes the doc and we fetch the one patient we actually need here, on demand.
  const [fetchedPatient, setFetchedPatient] = useState(null);
  const patient = patientProp || fetchedPatient;
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { showToast, ToastElement } = useToast();

  const handleReassigned = (newPatient) => {
    setShowReassign(false);
    showToast(`Скан перенесён пациенту «${newPatient.name}»`);
    onReassigned?.(newPatient);
    onClose();
  };

  // Бэкенд идемпотентен (POST возвращает уже существующий токен, если он есть), так что не
  // пытаемся угадать URL из doc.publicToken на клиенте — просто запрашиваем при клике.
  const [publicLink, setPublicLink] = useState(null);
  const [publicLinkLoading, setPublicLinkLoading] = useState(false);

  const handleGetPublicLink = async () => {
    setPublicLinkLoading(true);
    try {
      const r = await api.getPublicLink(doc.id);
      setPublicLink(r);
    } catch (e) {
      showToast(e.response?.data?.error || "Не удалось получить ссылку");
    } finally {
      setPublicLinkLoading(false);
    }
  };

  const handleRegeneratePublicLink = async () => {
    if (!(await confirm("Старая ссылка перестанет работать. Выпустить новую?"))) return;
    setPublicLinkLoading(true);
    try {
      const r = await api.regeneratePublicLink(doc.id);
      setPublicLink(r);
      showToast("Ссылка обновлена");
    } catch (e) {
      showToast(e.response?.data?.error || "Не удалось обновить ссылку");
    } finally {
      setPublicLinkLoading(false);
    }
  };

  const handleCopyPublicLink = () => {
    if (!publicLink?.url) return;
    navigator.clipboard.writeText(publicLink.url);
    showToast("Ссылка скопирована");
  };

  useEffect(() => {
    api.listDocumentFiles(doc.id).then(setAttachedFiles).catch(() => {});
  }, [doc.id]);

  useEffect(() => {
    if (patientProp || !doc.patientId) return;
    api.getPatient(doc.patientId).then(setFetchedPatient).catch(() => {});
  }, [doc.patientId, patientProp]);

  // Clean, background-removed, per-foot crops with the real lines/valik drawn on top — same
  // pipeline the saved report uses (lib/scanPreview.js), instead of showing the raw flatbed scan
  // (dark background, both feet, lots of empty margin) or the composited file (measurement lines
  // baked into its pixels, including the old "undefined мм" bug for region annotations).
  const [footCrops, setFootCrops] = useState(null);
  const [cropsFailed, setCropsFailed] = useState(false);
  useEffect(() => {
    if (!doc.rawFilename) { setCropsFailed(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const [rawResp, padSizes] = await Promise.all([
          fetch(api.rawFileUrl(doc.id)),
          api.listPadSizes().catch(() => []),
        ]);
        const blob = await rawResp.blob();
        const rawBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        const crops = await buildAnnotatedFootCrops(rawBase64, {
          annotations: doc.annotations || [],
          padSizes,
          padSizesPerFoot: doc.scanState?.padSizesPerFoot || {},
          dpi: doc.scanState?.dpi || 150,
        });
        if (!cancelled) {
          if (!crops.left && !crops.right) setCropsFailed(true);
          else setFootCrops(crops);
        }
      } catch {
        if (!cancelled) setCropsFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id, doc.rawFilename]);

  const handleDeleteFile = async (fileId) => {
    if (!(await confirm("Удалить файл без возможности восстановления?", { danger: true }))) return;
    await api.deleteDocumentFile(doc.id, fileId);
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // doc.reportHtml is absent from the light list payload (see docRowLight/patients.js scans) —
  // doc.hasReport tells us one exists without needing to ship the multi-hundred-KB HTML on every
  // list row, so fetch the real thing lazily here only when the user actually asks to open it.
  const handleOpenReport = async () => {
    let html = doc.reportHtml;
    if (html === undefined && doc.hasReport) {
      const full = await api.getDocument(doc.id);
      html = full.reportHtml;
    }
    if (html) {
      openSavedReport(html);
    } else {
      // Fallback: generate on-the-fly from stored data
      const footData = doc.footData || {
        left: { footSizeMm: doc.footSide === "left" ? doc.footSizeMm : null, diseases: doc.footSide === "left" ? (doc.diseases || []) : [] },
        right: { footSizeMm: doc.footSide === "right" ? doc.footSizeMm : null, diseases: doc.footSide === "right" ? (doc.diseases || []) : [] },
      };
      printReport({ imageUrl: fileUrl, patient, footData, generalSymptoms: doc.generalSymptoms, annotations: doc.annotations || [], dateScanned: doc.createdAt });
    }
  };

  const hasReport = doc.hasReport ?? !!doc.reportHtml;

  const handleDocCameraCapture = async (photo) => {
    const created = await api.uploadDocumentFiles(doc.id, [photo]);
    setAttachedFiles(prev => [...prev, ...created]);
  };

  return (
    <>
    {photoView === null && (
    <div
      className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
      onMouseDown={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col" style={{ maxWidth: 640, maxHeight: "85vh" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <span className="text-base font-bold text-slate-900">{doc.name}</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={handleOpenReport} title={hasReport ? "Открыть сохранённый отчёт" : "Сформировать отчёт"}>
              <FileText className="h-3.5 w-3.5" />
              {hasReport ? "Отчёт" : "PDF"}
            </Button>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setPhotoView("list")}>
              <Camera className="h-3.5 w-3.5" />
              Фото{attachedFiles.length > 0 ? ` (${attachedFiles.length})` : ""}
            </Button>
            {hasReport && (
              <Button variant="ghost" size="sm" className="gap-1.5" onClick={handleGetPublicLink} disabled={publicLinkLoading} title="Публичная ссылка на PDF-отчёт">
                <Link2 className="h-3.5 w-3.5" />
                Ссылка
              </Button>
            )}
            {onEdit && doc.rawFilename && (
              <Button variant="ghost" size="sm" className="gap-1.5" onClick={onEdit} title="Открыть скан на редактирование">
                <Pencil className="h-3.5 w-3.5" />
                Редактировать
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {publicLink?.url && (
          <div className="flex items-center gap-2 px-5 py-2 border-b border-slate-100 bg-slate-50 flex-shrink-0">
            <span className="flex-1 truncate text-xs text-slate-600 font-mono">{publicLink.url}</span>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={handleCopyPublicLink}>
              <Copy className="h-3.5 w-3.5" />
              Копировать
            </Button>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={handleRegeneratePublicLink} disabled={publicLinkLoading} title="Старая ссылка перестанет работать">
              <RefreshCw className="h-3.5 w-3.5" />
              Обновить
            </Button>
          </div>
        )}

        <div className="px-5 py-4 overflow-y-auto">
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mb-3">
            <span>{new Date(doc.createdAt).toLocaleString("ru-RU")}</span>
            {doc.footSizeMm && (
              <span>· Размер: <strong className="text-slate-700">{doc.footSizeMm} мм</strong> (RU {Math.round(doc.footSizeMm / 10 * 1.5 + 1)})</span>
            )}
            {doc.insoleType && (
              <span>· Стелька: <strong className="text-slate-700">{doc.insoleType}</strong></span>
            )}
            {doc.syncedAt && (
              <span>· Синхронизировано: <strong className="text-slate-700">{new Date(doc.syncedAt).toLocaleString("ru-RU")}</strong></span>
            )}
            {doc.location && (
              <span>· Точка: <strong className="text-slate-700">{doc.location}</strong></span>
            )}
            {doc.operatorName && (
              <span>· Провёл: <strong className="text-slate-700">{doc.operatorName}</strong></span>
            )}
          </div>

          {patient && (
            <div className="flex items-center gap-2 mb-3 text-sm">
              <span className="text-slate-400">Пациент:</span>
              <span className="font-semibold text-slate-900">{patient.name}<PatientAlias name={patient.name} alias={patient.alias} /></span>
              {onNavigateToPatient && (
                <Button
                  variant="ghost" size="sm" className="gap-1.5 h-6 px-2"
                  onClick={() => { onClose(); onNavigateToPatient(patient.id); }}
                >
                  Карточка
                  <ArrowRight className="h-3 w-3" />
                </Button>
              )}
              {user?.role === "admin" && user?.isCentral && (
                <Button
                  variant="ghost" size="sm" className="gap-1.5 h-6 px-2 text-slate-400 hover:text-slate-700"
                  onClick={() => setShowReassign(true)}
                  title="Перенести скан другому пациенту"
                >
                  <UserCog className="h-3 w-3" />
                  Перенести
                </Button>
              )}
            </div>
          )}

          {doc.diseases?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {doc.diseases.map(d => (
                <span key={d} className="inline-block px-2 py-0.5 bg-red-50 text-red-800 rounded-full text-[10px] font-medium">{d}</span>
              ))}
            </div>
          )}

          {(doc.noteComment || doc.noteRecommendations) && (
            <div className="flex flex-col gap-2 mb-3">
              {doc.noteComment && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Комментарий</span>
                  <span className="text-sm text-slate-700 whitespace-pre-wrap">{doc.noteComment}</span>
                </div>
              )}
              {doc.noteRecommendations && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Рекомендации</span>
                  <span className="text-sm text-slate-700 whitespace-pre-wrap">{doc.noteRecommendations}</span>
                </div>
              )}
            </div>
          )}

          {(doc.internalNote || patient?.internalNote) && (
            <div className="flex flex-col gap-2 mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Скрытые заметки (не в отчёт)</span>
              {doc.internalNote && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">По обследованию</span>
                  <span className="text-sm text-amber-900 whitespace-pre-wrap">{doc.internalNote}</span>
                </div>
              )}
              {patient?.internalNote && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">По пациенту</span>
                  <span className="text-sm text-amber-900 whitespace-pre-wrap">{patient.internalNote}</span>
                </div>
              )}
            </div>
          )}

          {/* Clean per-foot crops (background removed, lines/valik drawn) — left foot on the
              left, right on the right, cropped to the saved region box so no foot gets cut off
              and no dark scanner-bed margin is shown. Falls back to the plain composited file if
              a crop couldn't be built (e.g. an old scan with no region annotations at all). */}
          {footCrops?.left || footCrops?.right ? (
            <div className="w-full rounded-lg border border-slate-200 mb-3 overflow-hidden flex gap-px bg-slate-200" style={{ height: 300 }}>
              {footCrops.left && (
                <img src={footCrops.left} alt="Левая стопа" className="flex-1 h-full object-contain bg-white" />
              )}
              {footCrops.right && (
                <img src={footCrops.right} alt="Правая стопа" className="flex-1 h-full object-contain bg-white" />
              )}
            </div>
          ) : cropsFailed && !imgFailed && (
            <div className="w-full rounded-lg border border-slate-200 mb-3 overflow-hidden" style={{ height: 300 }}>
              <img
                src={doc.rawFilename ? api.rawFileUrl(doc.id) : fileUrl}
                alt={doc.name}
                className="w-full h-full object-cover"
                onError={() => setImgFailed(true)}
              />
            </div>
          )}

          {attachedFiles.length > 0 && (
            <div className="mb-4">
              <div className="text-sm font-semibold text-slate-900 mb-1.5">
                Файлы ({attachedFiles.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {attachedFiles.map((f, i) => (
                  <div key={f.id} className="relative group">
                    <button onClick={() => setPhotoView({ lightboxIndex: i })} className="block">
                      <FileThumb
                        src={api.documentFileUrl(doc.id, f.id)}
                        alt={f.originalName || "Фото"}
                        className="h-20 w-20 rounded-lg border border-slate-200"
                      />
                    </button>
                    <button
                      onClick={() => handleDeleteFile(f.id)}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Удалить файл"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    )}

    {photoView === "list" && (
      <PhotosModal
        docId={doc.id}
        files={attachedFiles}
        onFilesChange={setAttachedFiles}
        onCameraClick={() => setPhotoView("camera")}
        onPhotoClick={(i) => setPhotoView({ lightboxIndex: i })}
        onClose={() => setPhotoView(null)}
        confirm={confirm}
      />
    )}

    {photoView === "camera" && (
      <CameraCaptureModal
        onCapture={handleDocCameraCapture}
        onClose={() => setPhotoView("list")}
      />
    )}

    {photoView?.lightboxIndex != null && (
      <PhotoLightboxModal
        src={api.documentFileUrl(doc.id, attachedFiles[photoView.lightboxIndex]?.id)}
        alt={attachedFiles[photoView.lightboxIndex]?.originalName}
        onClose={() => setPhotoView("list")}
      />
    )}

      <ConfirmDialogElement />
      <ToastElement />
      {showReassign && (
        <ReassignPatientModal
          doc={doc}
          currentPatientName={patient?.name}
          onClose={() => setShowReassign(false)}
          onReassigned={handleReassigned}
        />
      )}
    </>
  );
}
